import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { login as apiLogin } from '../api';
import { useAuth } from '../context/AuthContext';
import { useConfig } from '../context/ConfigContext';
import { InfoAlert } from '../components/Alert';

function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  // WHERE TO GO BACK TO. Matches the shape AdminProtectedRoute already sends,
  // `state={{ from: location }}`, so there is one return-to convention in the
  // app rather than two. A plain login carries no state and still lands on the
  // dashboard, exactly as before.
  const from = location.state?.from?.pathname || '/dashboard';
  // Why the buyer is looking at a login form they did not ask for. Supplied by
  // whoever sent them here, so the explanation belongs to the flow that knows
  // the context -- this page does not invent one.
  const notice = location.state?.message || '';
  const { login } = useAuth();
  const { isDemoMode } = useConfig();
  const isDemo = isDemoMode();
  
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await apiLogin(formData);
      login(response.data.user, response.data.token);
      navigate(from);
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <h2>Login to Your Account</h2>

        {/* Not dismissible: it is the reason this page is on screen, and it has
            to still be there when the buyer looks up from typing a password. */}
        {notice && <InfoAlert message={notice} dismissible={false} />}

        {error && <div className="error-message">{error}</div>}
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Email:</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Password:</label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
            />
          </div>

          <button 
            type="submit" 
            className="btn btn-primary btn-full-width"
            disabled={loading}
          >
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>

        <div className="auth-links">
          {isDemo ? (
            <span className="demo-disabled-link" title="Disabled in demo mode">
              Forgot your password? <em>(Disabled in demo)</em>
            </span>
          ) : (
            <Link to="/forgot-password">Forgot your password?</Link>
          )}
        </div>
        <div className="auth-links">
          {isDemo ? (
            <span className="demo-disabled-link" title="Disabled in demo mode">
              Don't have an account? <em>(Registration disabled in demo)</em>
            </span>
          ) : (
            /* The return path has to survive this hop. A buyer part-way through
               a purchase on a new tenant's site is more likely to need an
               account than to have one, so this is the common path, not the
               edge case. */
            <>Don't have an account? <Link to="/register" state={location.state}>Register here</Link></>
          )}
        </div>
      </div>
    </div>
  );
}

export default Login;
